import React, { useState } from "react";
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Collapse,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const helpData = {
  VirtualCardHelp: [
    {
      question: "What is RuPay Virtual Prepaid Card?",
      answer: `RuPay Virtual Card is a limit Debit card, which can be created within the Wallet. The card can be used to shop online at any merchant website that accepts RuPay Cards, just like a regular plastic card.`,
    },
    {
      question: "Who can create RuPay Virtual Prepaid Card?",
      answer: `All customers of Wallet having Full KYC wallet rights can create the card online.`,
    },
    {
      question: "What is Authorization cooling period?",
      answer: `Once card generation request is raised, the wallet will take 2–3 working days to verify the details submitted and generate the RuPay prepaid card for your KYC details.`,
    },
    {
      question: "What is an e-commerce (online) transaction?",
      answer: `Electronic commerce, commonly known as e-commerce, refers to the buying and selling of products or services over electronic systems such as the Internet and other computer networks.`,
    },
    {
      question:
        "Do I require separate registration to avail Virtual Card facility?",
      answer: `No separate registration or enrolment is required, except that email verification is compulsory.`,
    },
    {
      question: "What is an OTP?",
      answer: `OTP is a One Time Password (6-digit number) generated by TA Wallet for validating card creation and for confirming merchant transactions.`,
    },
    {
      question: "What if I do not receive OTP?",
      answer: `Click on the link provided at the bottom of the webpage to resend OTP. If you still face issues, please contact customer care.`,
    },
    {
      question: "How many times OTP can be entered?",
      answer: `You can enter the OTP up to 3 times. After 3 consecutive failed attempts, the transaction will be declined.`,
    },
    {
      question: "Can I unblock my Virtual Card?",
      answer: `No, once the card is blocked, you cannot unblock or reuse the card.`,
    },
    {
      question: "When will my account get debited?",
      answer: `The wallet amount will be debited only when the actual purchase using the Virtual Card is successfully completed.`,
    },
    {
      question: "Can I withdraw Cash using my Virtual Card?",
      answer: `No, the card can only be used for making online payments on merchant websites that accept RuPay Prepaid Cards.`,
    },
    {
      question:
        "My prepaid card is locked since I entered incorrect PIN multiple times. How can I unblock it?",
      answer: `The card will automatically unlock after 24 hours.`,
    },
    {
      question:
        "Will I get SMS alerts for every transaction done using my prepaid card?",
      answer: `Yes, SMS alerts will be sent to your registered mobile number for every financial transaction.`,
    },
    {
      question:
        "Are the transactions done through RuPay prepaid cards secure? How are they authenticated?",
      answer: `Yes, RuPay prepaid card transactions are secure. Online transactions are authenticated by OTP delivered to the registered mobile number and verified through the RuPay Pay-Secure platform.`,
    },
    {
      question:
        "Where can I raise complaints or grievances related to prepaid cards?",
      answer: `You can write to: support@transactionanalysts.com or contact customer care.`,
    },
  ],
};

const VirtualCardHelp = ({ selectedSection = "LoadMoney" }) => {
  const questions = helpData[selectedSection] || [];
  const [openIndex, setOpenIndex] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const toggleAnswer = (index) => {
    setOpenIndex((prev) => (prev === index ? null : index));
  };

  return (
    <Box width="100%">
      {/* Top Banner */}
      <Box
        sx={{
          width: "100%",
          height: { xs: 180, sm: 220, md: 260 },
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: { xs: 1, sm: 3 },
        }}
      >
        <Box
          component="img"
          src="/Assets/marchantpayment.jpg"
          alt="Profile illustration"
          sx={{
            width: "100%",
            maxWidth: 1000,
            height: "100%",
            objectFit: "cover",
            borderRadius: "0 0 10px 10px",
          }}
        />
      </Box>

      {/* FAQ Heading */}
      <Box mt={2} display="flex" justifyContent="center">
        <Typography
          variant={isMobile ? "h6" : "h5"}
          fontWeight="bold"
          color="black"
        >
          Rupay Card
        </Typography>
      </Box>

      {/* FAQ List */}
      <Box
        sx={{
          width: "100%",
          maxWidth: "900px",
          mx: "auto",
          px: { xs: 1.5, sm: 2, md: 3 },
          mt: 2,
          py: 2,
        }}
      >
        <List disablePadding>
          {questions.map((item, index) => (
            <Box
              key={index}
              sx={{
                border: "1px solid #ddd",
                borderRadius: 2,
                mb: 2,
                overflow: "hidden",
                boxShadow:
                  openIndex === index ? "0 2px 8px rgba(0,0,0,0.06)" : "none",
                transition: "box-shadow 0.2s ease-in-out",
              }}
            >
              <ListItemButton
                onClick={() => toggleAnswer(index)}
                sx={{
                  borderBottom: "1px solid #eee",
                  px: 2,
                  py: 1.5,
                  borderRadius: 1,
                  backgroundColor:
                    openIndex === index ? "#e0f7fa" : "transparent",
                  "&:hover": {
                    backgroundColor:
                      openIndex === index ? "#b2ebf2" : "#f9f9f9",
                  },
                }}
              >
                <ListItemText primary={item.question} />
                {openIndex === index ? (
                  <ExpandMoreIcon sx={{ fontSize: 18 }} />
                ) : (
                  <ArrowForwardIosIcon sx={{ fontSize: 14 }} />
                )}
              </ListItemButton>

              <Collapse in={openIndex === index} timeout="auto" unmountOnExit>
                <Box px={3} py={2} bgcolor="#fafafa">
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}
                  >
                    {item.answer}
                  </Typography>
                </Box>
              </Collapse>
            </Box>
          ))}
        </List>
      </Box>
    </Box>
  );
};

export default VirtualCardHelp;
